export const WORKFLOW_DAG_AI_SYSTEM_PROMPT = `You are an expert workflow architect for PACMaster.

Goal: Generate a FULL workflow DAG definition JSON using ONLY the available handlers provided.

CRITICAL RULES:
- Output ONLY valid JSON.
- Do NOT include markdown, comments, or explanations.
- Do NOT include trailing commas.
- Use the exact field names specified.
- Do NOT invent extra top-level fields.

Return a JSON object with EXACTLY these top-level fields:
{
  "workflow_key": string,
  "workflow_description": string,
  "dag": object
}

The "dag" object MUST have AT LEAST these fields:
{
  "workflow_id": string,
  "inputs_schema": object,
  "outputs_schema": object,
  "outputs_bindings": object,
  "steps": object,
  "edges": array,
  "settings": object
}

DAG rules:
- workflow_id MUST equal workflow_key.
- inputs_schema and outputs_schema MUST be JSON Schema Draft 2020-12 objects.
- They MUST have: {"$schema":"https://json-schema.org/draft/2020-12/schema","type":"object","properties":{}}.
- Use these PACMaster value types ONLY (no v_ prefix): integer, floatpoint, string, boolean, jsonb, timestamp.
- Put human-friendly explanations into "description".
- Set "additionalProperties" to true unless the user explicitly says otherwise.

Steps rules:
- steps is a map keyed by step id (string) to a step definition object.
- Step ids MUST be unique and MUST be human-readable snake_case action names.
  - Good: download_youtube_audio, extract_metadata, upload_to_gcs
  - Avoid: step_1, step_2, task1
  - If you need to disambiguate duplicates, suffix with _2, _3, ...
- Each step must have the following shape:
{
  "type": "cloud" | "local",
  "service": string,
  "device_capability": string,
  "params_defaults": object,
  "params_bindings": object,
  "override_allowlist": array,
  "retries": { "count": number, "backoff": string }
}
- For cloud steps: set type="cloud" and device_capability="".
- For local steps: set type="local" and device_capability to a non-empty string if needed.
- service MUST be either:
  - an exact handler_key from the provided registry JSON, OR
  - a versionless handler family key suffixed with ":latest" (e.g. "worker.data_type.operation:latest").
    You may derive the versionless family key by stripping a trailing version suffix from a registry handler_key
    (supports legacy ".vN" and docker-like ":vN").
  - Do NOT invent handler keys or versions.
  - Prefer using the ":latest" form by default (unless the user explicitly requests pinning a specific version).
- params_bindings values may reference workflow inputs and other step outputs using ONLY these formats:
  - "{{inputs.<key>}}"
  - "{{steps.<step_id>.outputs.<output_key>}}"

Edges rules:
- edges is an array of objects {"from": string, "to": string}.
- Every edge endpoint MUST reference an existing step id.
- Use edges to model necessary sequencing.

Outputs binding rules:
- outputs_bindings maps workflow output keys to binding strings.
- Each value MUST be a NON-EMPTY string and must be one of:
  - "{{inputs.<key>}}"
  - "{{steps.<step_id>.outputs.<output_key>}}"
- outputs_bindings MUST include an entry for every key defined in outputs_schema.properties.

Settings rules:
- settings MUST include at least:
  - max_concurrency (number)
  - timeout_seconds (number)
  - default_retry { count, backoff }

You will be given:
- the user's workflow description in plain text
- the handler registry context for available services (may be summarized/truncated)

Use the handler registry to choose appropriate handlers, create steps, add edges, and wire inputs/outputs through params_bindings and outputs_bindings.`;

export type WorkflowDagAiContext = {
  handlers_json?: unknown;
  /** Soft cap for the resulting prompt length. Default: 120k characters. */
  max_chars?: number;
};

export function buildWorkflowDagAiUserPrompt(plainTextSpec: string, ctx?: WorkflowDagAiContext): string {
  const maxChars = (ctx?.max_chars && Number.isFinite(ctx.max_chars) && ctx.max_chars > 1000)
    ? Math.floor(ctx.max_chars)
    : 120_000;

  const handlersBlock = ctx?.handlers_json !== undefined
    ? `\n\n${buildHandlerRegistryContext(ctx.handlers_json, { maxChars: Math.floor(maxChars * 0.65) })}`
    : '';

  const base = `WORKFLOW SPEC (plain text):\n${plainTextSpec.trim()}\n\nYou MUST:\n1) Create steps using ONLY available handlers (service=handler_key).\n2) Create edges if needed.\n3) Wire workflow IO and steps via params_bindings and outputs_bindings.\n\nAlso return workflow_key and workflow_description.\n\nReturn ONLY the JSON object described in the system prompt.`;

  const prompt = `${base}${handlersBlock}`;
  if (prompt.length <= maxChars) return prompt;

  // Final safety: hard-trim in case upstream spec is huge.
  const tail = '\n\n[NOTE] Context truncated due to size limits.';
  const keep = Math.max(0, maxChars - tail.length);
  return `${prompt.slice(0, keep)}${tail}`;
}

function safeJson(value: unknown): string {
  try {
    return JSON.stringify(value ?? null, null, 2);
  } catch {
    return 'null';
  }
}

function buildHandlerRegistryContext(handlersJson: unknown, opts: { maxChars: number }): string {
  const maxChars = Number.isFinite(opts.maxChars) && opts.maxChars > 1000 ? Math.floor(opts.maxChars) : 60_000;

  // If the shape is unknown, fall back to JSON (truncated).
  if (!Array.isArray(handlersJson)) {
    const raw = safeJson(handlersJson);
    if (raw.length <= maxChars) return `HANDLER REGISTRY JSON:\n${raw}`;
    return `HANDLER REGISTRY JSON (truncated):\n${raw.slice(0, Math.max(0, maxChars - 80))}\n...`;
  }

  const handlers = handlersJson
    .filter((h) => h && typeof h === 'object' && !Array.isArray(h))
    .map((h) => h as Record<string, any>);

  const keys = handlers
    .map((h) => String(h.handler_key ?? '').trim())
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));

  const keysText = `HANDLER KEYS (service MUST be one of these OR use "<base>:latest"): (${keys.length})\n${keys.join('\n')}`;

  // Compact per-handler capabilities. This is best-effort and may be truncated.
  const lines: string[] = [];
  for (const h of handlers
    .slice()
    .sort((a, b) => String(a.handler_key ?? '').localeCompare(String(b.handler_key ?? '')))) {
    const handler_key = String(h.handler_key ?? '').trim();
    if (!handler_key) continue;

    const description = truncateOneLine(String(h.description ?? '').trim(), 160);
    const cfg = coerceObject(normalizeHandlerConfig(h.config));
    const paramsSchema = coerceObject(cfg.params_schema);
    const mandatory = Object.keys(coerceObject(paramsSchema.mandatory)).sort();
    const optional = Object.keys(coerceObject(paramsSchema.optional)).sort();

    const outputs: string[] = (() => {
      const outputsSchema = coerceObject(cfg.outputs_schema);
      const props = coerceObject(outputsSchema.properties);
      const fromSchema = Object.keys(props);
      if (fromSchema.length) return fromSchema.sort();
      const arr = Array.isArray((cfg as any).outputs) ? (cfg as any).outputs : [];
      return arr.filter((x: any) => typeof x === 'string' && x.trim()).map((x: string) => x.trim()).sort();
    })();

    const inKeys = [...mandatory.map((k) => `+${k}`), ...optional.map((k) => `?${k}`)].slice(0, 30);
    const outKeys = outputs.slice(0, 30);

    const line = `- ${handler_key}`
      + (description ? ` | ${description}` : '')
      + (inKeys.length ? ` | in: ${inKeys.join(', ')}` : '')
      + (outKeys.length ? ` | out: ${outKeys.join(', ')}` : '');

    lines.push(line);
  }

  const header = 'HANDLER CAPABILITIES (compact; best-effort; may be truncated):';
  const joinedLines: string[] = [];
  let used = 0;
  for (const ln of lines) {
    const next = (joinedLines.length ? 1 : 0) + ln.length;
    if (used + next > Math.max(0, maxChars - keysText.length - header.length - 50)) break;
    joinedLines.push(ln);
    used += next;
  }

  const capabilitiesText = joinedLines.length
    ? `${header}\n${joinedLines.join('\n')}${joinedLines.length < lines.length ? `\n... (${lines.length - joinedLines.length} more handlers omitted)` : ''}`
    : `${header}\n(none)`;

  // Assemble with a soft cap.
  const out = `${keysText}\n\n${capabilitiesText}`;
  if (out.length <= maxChars) return out;
  return `${out.slice(0, Math.max(0, maxChars - 80))}\n... (truncated)`;
}

function normalizeHandlerConfig(config: unknown): unknown {
  if (config === undefined || config === null) return {};
  if (typeof config === 'string') {
    const trimmed = config.trim();
    if (!trimmed) return {};
    try {
      return JSON.parse(trimmed);
    } catch {
      return {};
    }
  }

  if (config && typeof config === 'object' && !Array.isArray(config)) {
    const obj = config as Record<string, any>;
    const looksLikeHandlerConfig = (v: unknown): boolean => {
      if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
      const vv = v as Record<string, any>;
      return typeof vv.params_schema === 'object' || typeof vv.outputs_schema === 'object' || Array.isArray(vv.outputs);
    };

    if (looksLikeHandlerConfig(obj)) return obj;

    const innerConfig = obj.config;
    if (looksLikeHandlerConfig(innerConfig)) return innerConfig;

    const innerHandlerConfig = obj.handler_config;
    if (looksLikeHandlerConfig(innerHandlerConfig)) return innerHandlerConfig;

    if (innerConfig && typeof innerConfig === 'object' && !Array.isArray(innerConfig)) {
      const nested = (innerConfig as Record<string, any>).handler_config;
      if (looksLikeHandlerConfig(nested)) return nested;
    }
  }

  return config;
}

function coerceObject(value: unknown): Record<string, any> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, any>) : {};
}

function truncateOneLine(text: string, maxLen: number): string {
  const t = String(text ?? '').replace(/[\r\n\t]+/g, ' ').trim();
  if (!t) return '';
  if (t.length <= maxLen) return t;
  return `${t.slice(0, Math.max(0, maxLen - 1)).trim()}…`;
}
